import { useMemo } from 'react';

import { ISelectOption } from '@private/components';
import { StringParam, useQueryParams } from 'use-query-params';

import {
  FILTER_OPERATIONS,
  IAppliedFilterValue,
} from 'components/Grids/DataGrid/Filters/utils/types';
import { DICTIONARY_TYPE, useGetDictionaryNaming } from 'hooks/useGetDictionaryNaming';
import { DELIMITER } from 'utils/filters';
import { INullable } from 'utils/types';

const operations = Object.values(FILTER_OPERATIONS).sort((a, b) => b.length - a.length);

type TSearchFilter = Pick<
  IAppliedFilterValue,
  'additionalCondition' | 'operation' | 'value'
>;
type TSearchQuery = Record<string, TSearchFilter>;

const isLikeOperation = (operation?: INullable<FILTER_OPERATIONS>) =>
  operation === FILTER_OPERATIONS.LIKE || operation === FILTER_OPERATIONS.NOT_LIKE;

const composeSameColumnFilters = <T extends TSearchFilter>(
  currentFilter: T,
  newFilter: T,
): TSearchFilter => {
  if (currentFilter.operation === newFilter.operation) {
    const newValue = Array.isArray(currentFilter.value)
      ? [...currentFilter.value, newFilter.value]
      : [currentFilter.value, newFilter.value];
    return {
      ...currentFilter,
      operation: currentFilter.operation,
      value: newValue as string[],
    };
  }

  const { additionalCondition } = currentFilter;

  if (additionalCondition && additionalCondition.operation === newFilter.operation) {
    const additionalValue = Array.isArray(additionalCondition.value)
      ? [...additionalCondition.value, newFilter.value]
      : [additionalCondition.value, newFilter.value];
    return {
      ...currentFilter,
      additionalCondition: { ...additionalCondition, value: additionalValue as string[] },
    };
  }

  if (isLikeOperation(currentFilter.operation) && isLikeOperation(newFilter.operation)) {
    return {
      ...currentFilter,
      additionalCondition: {
        operation: newFilter.operation,
        value: [newFilter.value] as string[],
      },
    };
  }

  if (
    newFilter.operation === FILTER_OPERATIONS.LESS_OR_EQUALS &&
    currentFilter.operation === FILTER_OPERATIONS.MORE_OR_EQUALS
  ) {
    return {
      operation: FILTER_OPERATIONS.RANGE,
      value: { max: newFilter.value, min: currentFilter.value },
    } as T;
  }

  if (
    newFilter.operation === FILTER_OPERATIONS.MORE_OR_EQUALS &&
    currentFilter.operation === FILTER_OPERATIONS.LESS_OR_EQUALS
  ) {
    return {
      operation: FILTER_OPERATIONS.RANGE,
      value: { max: currentFilter.value, min: newFilter.value },
    } as T;
  }

  return {
    ...currentFilter,
    ...newFilter,
  };
};

const normalizeCommaSeparatedValue = (
  values: string[],
  column: string,
  getPaymentDictionaryNaming: ReturnType<typeof useGetDictionaryNaming>,
  getAnalyticsDictionaryNaming: ReturnType<typeof useGetDictionaryNaming>,
): ISelectOption[] => {
  return values.map((value) => {
    const paymentDictionaryValue = getPaymentDictionaryNaming(column, value);
    const analyticsDictionaryValue = getAnalyticsDictionaryNaming(column, value);

    return {
      label:
        analyticsDictionaryValue === value
          ? paymentDictionaryValue
          : analyticsDictionaryValue,
      value,
    };
  });
};

export const useUrlSearchQuery = (): null | TSearchQuery => {
  const [{ search: urlSearchQuery }] = useQueryParams({ search: StringParam });
  const getPaymentDictionaryNaming = useGetDictionaryNaming({
    disableNormalize: true,
  });
  const getAnalyticsDictionaryNaming = useGetDictionaryNaming({
    dictionaryType: DICTIONARY_TYPE.ANALYTICS,
    disableNormalize: true,
  });

  return useMemo<null | TSearchQuery>(() => {
    if (!urlSearchQuery) {
      return null;
    }

    const searchQuery = urlSearchQuery.split(DELIMITER).reduce((acc, filter: string) => {
      const operation = operations.find((op) => filter.includes(op));

      if (!operation) {
        // eslint-disable-next-line no-console
        console.error(`Operation not found in filter: ${filter}`);
        return acc;
      }

      const [columnKey, value] = filter.split(operation);

      const newFilter = {
        operation,
        value,
      };

      const isAlreadyExist = !!(acc[columnKey] as TSearchFilter | undefined);
      if (isAlreadyExist && newFilter.value) {
        /*
          handle filters:
          name~ADP,name~ADA => { name: { operation: '~', value: [{ label: 'ADA', value: 'ADA' }, { label: 'ADP', value: 'ADP' }] } }
          created>2024-01-01,created<2024-12-01 => { created: { operation: '<>', value: { min: '2024-01-01', max: '2024-12-01' } }
        */
        acc[columnKey] = composeSameColumnFilters(acc[columnKey], newFilter);
        return acc;
      }

      const valueSplittedByComma = newFilter.value.split(';');
      if (valueSplittedByComma.length > 1) {
        /*
          handle filters:
          currency#ADA;ADP => { currency: { operation: '#', value: [{ label: 'ADA', value: 'ADA' }, { label: 'ADP', value: 'ADP' }] } }
        */
        acc[columnKey] = {
          ...newFilter,
          value: normalizeCommaSeparatedValue(
            valueSplittedByComma,
            columnKey,
            getPaymentDictionaryNaming,
            getAnalyticsDictionaryNaming,
          ),
        };
        return acc;
      }

      acc[columnKey] = newFilter;
      return acc;
    }, {} as TSearchQuery);

    return Object.entries(searchQuery).length ? searchQuery : null;
  }, [getAnalyticsDictionaryNaming, getPaymentDictionaryNaming, urlSearchQuery]);
};
